import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import PublicHeader from '../../components/Layout/PublicHeader';
import PublicFooter from '../../components/Layout/PublicFooter';
import api from '../../utils/axiosConfig';
import { getImageUrl } from '../../utils/imageUtils';

interface BlogPost {
    _id: string;
    title: string;
    slug: string;
    excerpt: string;
    featuredImage?: string;
    category: string;
    tags: string[];
    author: {
        name: string;
        email?: string;
        avatar?: string;
    };
    readTime: number;
    publishedAt?: string;
    views: number;
}

const Blog: React.FC = () => {
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState<string>('All');
    const [blogs, setBlogs] = useState<BlogPost[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchBlogs();
    }, []);

    const fetchBlogs = async () => {
        try {
            setLoading(true);
            const response = await api.get('/blogs');
            setBlogs(response.data.data || []);
        } catch (error: any) {
            console.error('Failed to fetch blogs:', error);
        } finally {
            setLoading(false);
        }
    };

    const categories = ['All', ...Array.from(new Set(blogs.map(post => post.category)))];

    const filteredPosts = blogs.filter(post => {
        const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
            post.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesCategory = selectedCategory === 'All' || post.category === selectedCategory;
        return matchesSearch && matchesCategory;
    });

    return (
        <div className="min-h-screen bg-white">
            <PublicHeader />

            {/* Hero Section */}
            <section className="pt-32 pb-24 bg-gradient-to-br from-blue-50/40 via-white to-orange-50/30 relative overflow-hidden">
                <div className="absolute inset-0 overflow-hidden pointer-events-none">
                    <div className="absolute -top-40 -right-40 w-96 h-96 bg-[#3B82F6]/10 rounded-full blur-3xl"></div>
                    <div className="absolute top-60 -left-40 w-96 h-96 bg-[#F59E0B]/10 rounded-full blur-3xl"></div>
                </div>
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <div className="max-w-5xl mx-auto text-center">
                        <div className="inline-flex items-center px-4 py-2 bg-blue-50 border border-blue-100 rounded-full mb-8">
                            <span className="w-2 h-2 bg-[#3B82F6] rounded-full mr-2 animate-pulse"></span>
                            <span className="text-sm font-medium text-[#3B82F6]">Latest Insights</span>
                        </div>
                        <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold mb-8 leading-tight">
                            <span className="text-[#3B3B3B] inline">Our </span>
                            <span className="text-[#3B82F6] inline bg-gradient-to-r from-[#3B82F6] to-blue-500 bg-clip-text text-transparent">Blog</span>
                        </h1>
                        <p className="text-xl sm:text-2xl text-gray-600 leading-relaxed max-w-3xl mx-auto mb-8">
                            Insights, tips, and strategies to help your business grow through effective marketing
                        </p>

                        {/* Search Bar */}
                        <div className="max-w-2xl mx-auto">
                            <div className="relative">
                                <input
                                    type="text"
                                    placeholder="Search articles..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="w-full px-6 py-4 pl-14 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#3B82F6] focus:border-transparent text-[#3B3B3B] transition-all"
                                />
                                <svg className="absolute left-5 top-1/2 transform -translate-y-1/2 w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Category Filter */}
            <section className="py-8 bg-white border-b border-gray-100">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-wrap gap-3 justify-center">
                        {categories.map(category => (
                            <button
                                key={category}
                                onClick={() => setSelectedCategory(category)}
                                className={`px-6 py-2 rounded-full font-medium transition-all transform hover:scale-105 ${selectedCategory === category
                                    ? 'bg-[#3B82F6] text-white shadow-lg'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }`}
                            >
                                {category}
                            </button>
                        ))}
                    </div>
                </div>
            </section>

            {/* Blog Posts Grid */}
            <section className="py-20 bg-gray-50">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                    {loading ? (
                        <div className="text-center py-20">
                            <div className="spinner mx-auto"></div>
                            <p className="mt-4 text-gray-600">Loading blog posts...</p>
                        </div>
                    ) : filteredPosts.length === 0 ? (
                        <div className="text-center py-20">
                            <svg className="w-24 h-24 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <h3 className="text-xl font-bold text-gray-700 mb-2">No posts found</h3>
                            <p className="text-base text-gray-600">Try adjusting your search or filter criteria</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {filteredPosts.map((post) => (
                                <article
                                    key={post._id}
                                    className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-gray-100 hover:border-[#3B82F6] hover:-translate-y-2"
                                >
                                    <div className="relative h-48 overflow-hidden">
                                        {post.featuredImage ? (
                                            <img
                                                src={getImageUrl(post.featuredImage)}
                                                alt={post.title}
                                                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                                            />
                                        ) : (
                                            <div className="w-full h-full bg-gradient-to-br from-[#3B82F6] to-blue-600">
                                                <div className="absolute inset-0 bg-gradient-to-br from-[#3B82F6]/50 to-[#F59E0B]/50 group-hover:scale-110 transition-transform duration-300"></div>
                                            </div>
                                        )}
                                        <div className="absolute bottom-4 left-4">
                                            <span className="inline-block px-3 py-1 bg-white/90 backdrop-blur-sm text-[#3B82F6] rounded-full text-sm font-semibold">
                                                {post.category}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="p-6">
                                        <div className="flex items-center text-sm text-gray-500 mb-3">
                                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                            </svg>
                                            <span>{post.publishedAt ? new Date(post.publishedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : 'Not published'}</span>
                                            <span className="mx-2">•</span>
                                            <span>{post.readTime} min read</span>
                                        </div>
                                        <h3 className="text-lg font-bold text-[#3B3B3B] mb-3 group-hover:text-[#3B82F6] transition-colors line-clamp-2">
                                            {post.title}
                                        </h3>
                                        <p className="text-base text-gray-600 mb-4 line-clamp-3">
                                            {post.excerpt}
                                        </p>
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm font-medium text-gray-700">
                                                {post.author.name}
                                            </span>
                                            <Link
                                                to={`/blog/${post.slug}`}
                                                className="inline-flex items-center text-[#3B82F6] font-semibold hover:text-blue-700 group-hover:gap-2 transition-all"
                                            >
                                                Read More
                                                <svg className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                                                </svg>
                                            </Link>
                                        </div>
                                    </div>
                                </article>
                            ))}
                        </div>
                    )}
                </div>
            </section>

            <PublicFooter />
        </div>
    );
};

export default Blog;
